export const FILENAME_PREFIX = 'evoq-enhanced';
export const VERSION_PREFIX = 'v';

/**
 * Generates a new, enhanced filename based on a source filename.
 * If a sourceUrl is provided, the extension is extracted from the URL.
 * Otherwise, the original extension from sourceFilename is preserved.
 *
 * @param sourceFilename - The original filename, including its extension (e.g., 'plan-resize.jpg').
 * @param sourceUrl - An optional URL from which to extract the file extension (e.g., a CDN URL ending in .mp4).
 * @param version - An optional version number to append to the filename.
 * @returns The newly generated filename (e.g., 'evoq-enhanced-plan-resize-v2.mp4'). Returns an empty string if the source filename is empty or has no extension.
 *
 * @example
 * // Returns 'evoq-enhanced-plan-resize-v2.jpg'
 * generateEnhancedFilename('plan-resize.jpg', undefined, 2);
 *
 * @example
 * // Returns 'evoq-enhanced-plan-resize.jpg'
 * generateEnhancedFilename('plan-resize.jpg');
 *
 * @example
 * // Uses extension from sourceUrl instead of sourceFilename
 * // Returns 'evoq-enhanced-resizze.mp4'
 * generateEnhancedFilename('resizze.jpg', 'https://cdn.fourniergroupe.com/evoq-videos.test/86caa272-217f-42c1-8a11-61d246425b33.mp4');
 *
 * @example
 * // Handles filenames with multiple dots
 * // Returns 'evoq-enhanced-archive.v1-v3.jpeg'
 * generateEnhancedFilename('archive.v1.jpeg', undefined, 3);
 */
export const generateEnhancedFilename = (sourceFilename: string, sourceUrl?: string, version?: number): string => {
  // Guard clause for empty input
  if (!sourceFilename) {
    return '';
  }

  // Extract the original extension and name by finding the last dot
  const lastDotIndex = sourceFilename.lastIndexOf('.');

  // If no extension found, return empty string
  if (lastDotIndex === -1) {
    return '';
  }

  const originalName = sourceFilename.substring(0, lastDotIndex);

  // Determine the final extension: from sourceUrl if provided, otherwise from sourceFilename
  let finalExtension: string;
  if (sourceUrl) {
    // Extract extension from URL (handle query params by getting pathname first)
    try {
      const url = new URL(sourceUrl);
      const pathname = url.pathname;
      const urlLastDotIndex = pathname.lastIndexOf('.');
      if (urlLastDotIndex !== -1) {
        finalExtension = pathname.substring(urlLastDotIndex + 1);
      } else {
        // Fallback to original extension if URL has no extension
        finalExtension = sourceFilename.substring(lastDotIndex + 1);
      }
    } catch {
      // If URL parsing fails, fallback to original extension
      finalExtension = sourceFilename.substring(lastDotIndex + 1);
    }
  } else {
    finalExtension = sourceFilename.substring(lastDotIndex + 1);
  }

  const nameParts: string[] = [FILENAME_PREFIX, originalName];

  // Append version suffix if provided and valid
  if (typeof version === 'number' && version > 0) {
    nameParts.push(`${VERSION_PREFIX}${version}`);
  }

  const baseName = nameParts.join('-');

  return `${baseName}.${finalExtension}`;
};
